import { useEffect } from "react";
import { useMap } from "react-leaflet";

const TOOLTIP_SELECTOR =
  ".leaflet-tooltip-top.map-marker, .leaflet-tooltip-bottom.map-marker";

/**
 * Corrects Leaflet tooltip horizontal positioning for `.map-marker` tooltips.
 *
 * Leaflet's default transform doesn't account for variable tooltip widths,
 * so we patch the `translateX` value based on each tooltip's measured width.
 * A single MutationObserver on the map container handles all tooltips.
 */
const applyTooltipTransform = (el: HTMLElement) => {
  const currentTransform = el.style.transform;
  const halfWidth = el.offsetWidth / 2;

  if (!currentTransform || halfWidth <= 0) return;

  const isBottom = el.classList.contains("leaflet-tooltip-bottom");
  const expectedTranslate = isBottom
    ? ` translateX(${halfWidth}px)`
    : ` translateX(-${halfWidth}px)`;

  if (!currentTransform.includes(expectedTranslate)) {
    const cleanTransform = currentTransform.replace(
      / translateX\([^)]+\)/g,
      "",
    );
    el.style.transform = `${cleanTransform}${expectedTranslate}`;
  }
};

export const useTooltipTransformFix = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    // Apply to all existing tooltips on mount
    container
      .querySelectorAll<HTMLElement>(TOOLTIP_SELECTOR)
      .forEach(applyTooltipTransform);

    // Single observer for all tooltip style mutations
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const el = mutation.target as HTMLElement;
        if (
          el.classList.contains("map-marker") &&
          (el.classList.contains("leaflet-tooltip-top") ||
            el.classList.contains("leaflet-tooltip-bottom"))
        ) {
          applyTooltipTransform(el);
        }
      });
    });

    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => observer.disconnect();
  }, [map]);
};
